import { motion } from "framer-motion";
import { Gem, Sparkles, UserCheck, Package } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

const meta = [
  { icon: Gem, slug: "white", index: "01" },
  { icon: Sparkles, slug: "colored", index: "02" },
  { icon: UserCheck, slug: "private", index: "03" },
  { icon: Package, slug: "wholesale", index: "04" },
];

export const NaturalDiamonds = () => {
  const { t } = useT();
  const cards = t("diamonds.cards");

  return (
    <section
      id="expert-focus"
      data-testid={TEST_IDS.diamonds.root}
      className="relative py-28 md:py-40 bg-gradient-to-b from-soft via-white to-soft"
    >
      <div className="container-luxe">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12"
        >
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[11px] tracking-[0.4em] text-gold">
                {t("diamonds.index")}
              </span>
              <div className="h-px w-12 bg-gold" />
              <span className="overline">{t("diamonds.overline")}</span>
            </div>
            <h2
              data-testid={TEST_IDS.diamonds.heading}
              className="h-display text-4xl md:text-5xl lg:text-6xl text-ink text-balance"
            >
              {t("diamonds.title1")}
              <br />
              <span className="italic">{t("diamonds.title2")}</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4 space-y-5">
            <p className="text-base md:text-lg leading-relaxed text-ink/85 font-light">
              {t("diamonds.body1")}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-muted_text font-light">
              {t("diamonds.body2")}
            </p>
          </div>
        </motion.div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {meta.map((m, i) => {
            const Icon = m.icon;
            const card = cards[i];
            return (
              <motion.div
                key={m.slug}
                data-testid={TEST_IDS.diamonds.card(m.slug)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
                className="group relative p-8 lg:p-10 backdrop-blur-xl bg-white/70 border border-white/70 shadow-glass hover:shadow-soft-lift transition-all duration-700 overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-ice_blue/40 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                <div className="flex items-center justify-between mb-10">
                  <span className="font-mono text-[11px] tracking-[0.4em] text-muted_text">
                    {m.index}
                  </span>
                  <div className="w-11 h-11 flex items-center justify-center border border-ink/15 group-hover:border-gold group-hover:bg-gold/5 transition-all duration-500">
                    <Icon
                      className="w-4 h-4 text-ink group-hover:text-gold transition-colors duration-500"
                      strokeWidth={1.2}
                    />
                  </div>
                </div>

                <h3 className="font-display text-2xl leading-tight text-ink mb-4 text-balance">
                  {card.title}
                </h3>
                <p className="text-[14px] leading-relaxed text-muted_text font-light">
                  {card.body}
                </p>

                <div className="mt-8 flex items-center justify-between">
                  <div className="h-px flex-1 bg-ink/10 group-hover:bg-gold/40 transition-colors duration-500" />
                  <span className="ml-4 font-mono text-[10px] uppercase tracking-[0.3em] text-muted_text">
                    Coumba Kandji
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
